import React, { useEffect, useState } from "react";
import { Card, Row, Col, Typography, Space } from "antd";
import {
  DollarOutlined,
  ShoppingCartOutlined,
  AppstoreOutlined,
  WarningOutlined,
  BoxPlotOutlined,
  RiseOutlined,
} from "@ant-design/icons";

import api from "../../../api/api";
import { useYear } from "../../../api/context/YearContext";

const { Title, Text } = Typography;

const DashboardCards = ({ setActiveComponent }) => {
  const { selectedYear } = useYear();

  const [summary, setSummary] = useState({});
  const [lowStock, setLowStock] = useState(0);
  const [revenue, setRevenue] = useState(0);

  // =========================================================
  // FETCH SUMMARY
  // =========================================================

  const fetchSummary = async () => {
    try {
      const res = await api.get("/product-summary/");
      setSummary(res.data);
    } catch (error) {
      console.error("Failed to fetch product summary:", error);
    }
  };

  // =========================================================
  // FETCH LOW STOCK
  // =========================================================

  const fetchLowStock = async () => {
    try {
      const res = await api.get("/low-stock/");
      setLowStock(res.data.low_stock_count || 0);
    } catch (error) {
      console.error("Failed to fetch low stock:", error);
    }
  };

  // =========================================================
  // FETCH REVENUE
  // =========================================================

  const fetchRevenue = async () => {
    try {
      const res = await api.get(
        `/purchase-total/?year_id=${selectedYear?.id || ""}`
      );

      setRevenue(res.data.total_revenue || 0);
    } catch (error) {
      console.error("Failed to fetch revenue:", error);
    }
  };

  // =========================================================
  // INITIAL LOAD
  // =========================================================

  useEffect(() => {
    fetchSummary();
    fetchLowStock();
  }, []);

  // =========================================================
  // YEAR CHANGE
  // =========================================================

  useEffect(() => {
    if (selectedYear) {
      fetchRevenue();
    }
  }, [selectedYear]);

  // =========================================================
  // LIVE REFRESH
  // =========================================================

  useEffect(() => {
    const interval = setInterval(() => {
      fetchSummary();
      fetchLowStock();

      if (selectedYear) {
        fetchRevenue();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [selectedYear]);

  // =========================================================
  // MONEY FORMAT
  // =========================================================

  const formatMoney = (val) =>
    Number(val || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  // =========================================================
  // NUMBER FORMAT
  // =========================================================

  const formatNumber = (val) =>
    Number(val || 0).toLocaleString();

  // =========================================================
  // SUMMARY VALUES
  // =========================================================

  const totalStockValue = Number(
    summary.overall_total_value || 0
  );

  const totalProducts = Number(
    summary.total_products || 0
  );

  const totalUnits = Number(
    summary.total_units_in_stock || 0
  );

  // =========================================================
  // CARDS
  // =========================================================

  const cards = [
    {
      type: "revenue",
      icon: <DollarOutlined />,
      value: `$${formatMoney(revenue)}`,
      label: `Revenue (${selectedYear?.name || "Year"})`,
      bg: "#0B3D2E",
    },

    {
      type: "stock",
      icon: <ShoppingCartOutlined />,
      value: `$${formatMoney(totalStockValue)}`,
      label: "Total Stock Selling Value",
      bg: "#1C1F4A",
    },

    {
      type: "products",
      icon: <AppstoreOutlined />,
      value: formatNumber(totalProducts),
      label: "Products",
      bg: "#4A2C5A",
      action: "productlist",
    },

    {
      type: "lowstock",
      icon: <WarningOutlined />,
      value: formatNumber(lowStock),
      label: "Low Stock",
      bg: "#5A1E1E",
    },
  ];

  return (
    <Row gutter={[16, 16]}>
      {cards.map((c, i) => (
        <Col
          xs={24}
          sm={12}
          lg={c.type === "stock" ? 12 : 4}
          key={i}
        >
          <Card
            onClick={() =>
              c.action && setActiveComponent(c.action)
            }
            style={{
              background: c.bg,
              color: "#fff",
              borderRadius: 16,
              cursor: c.action ? "pointer" : "default",
              height: "100%",
              border: "none",
              boxShadow:
                "0 8px 24px rgba(0,0,0,0.12)",
            }}
            bodyStyle={{
              padding: 20,
            }}
          >
            {/* =================================================
                NORMAL CARDS
            ================================================= */}

            {c.type !== "stock" && (
              <>
                <div
                  style={{
                    fontSize: 28,
                    marginBottom: 10,
                    color: "rgba(255,255,255,0.9)",
                  }}
                >
                  {c.icon}
                </div>

                <Title
                  level={3}
                  style={{
                    color: "#fff",
                    margin: 0,
                    marginBottom: 6,
                  }}
                >
                  {c.value}
                </Title>

                <Text
                  style={{
                    color:
                      "rgba(255,255,255,0.7)",
                    fontSize: 14,
                  }}
                >
                  {c.label}
                </Text>
              </>
            )}

            {/* =================================================
                LARGE TOTAL STOCK CARD
            ================================================= */}

            {c.type === "stock" && (
              <div>
                {/* HEADER */}

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 12,
                  }}
                >
                  <Space size={10}>
                    <div
                      style={{
                        width: 42,
                        height: 42,
                        borderRadius: 12,
                        background:
                          "rgba(255,255,255,0.12)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 22,
                      }}
                    >
                      {c.icon}
                    </div>

                    <div>
                      <Text
                        style={{
                          color:
                            "rgba(255,255,255,0.7)",
                          fontSize: 13,
                        }}
                      >
                        TOTAL STOCK
                      </Text>

                      <div>
                        <Text
                          strong
                          style={{
                            color: "#fff",
                            fontSize: 17,
                          }}
                        >
                          Selling Value
                        </Text>
                      </div>
                    </div>
                  </Space>

                  <RiseOutlined
                    style={{
                      fontSize: 24,
                      color:
                        "rgba(255,255,255,0.6)",
                    }}
                  />
                </div>

                {/* MAIN VALUE */}

                <Title
                  level={1}
                  style={{
                    color: "#fff",
                    margin: "4px 0 2px",
                    fontSize: 34,
                    lineHeight: 1.2,
                  }}
                >
                  ${formatMoney(totalStockValue)}
                </Title>

                <Text
                  style={{
                    color:
                      "rgba(255,255,255,0.65)",
                    fontSize: 13,
                  }}
                >
                  Potential selling value of all
                  current stock
                </Text>

                {/* DIVIDER */}

                <div
                  style={{
                    height: 1,
                    background:
                      "rgba(255,255,255,0.12)",
                    margin:
                      "18px 0 14px",
                  }}
                />

                {/* STATISTICS */}

                <Row gutter={[12, 12]}>
                  {/* PRODUCTS */}

                  <Col xs={12} sm={8}>
                    <div
                      style={{
                        background:
                          "rgba(255,255,255,0.08)",
                        borderRadius: 10,
                        padding: 10,
                      }}
                    >
                      <Space size={7}>
                        <AppstoreOutlined
                          style={{
                            fontSize: 16,
                            color:
                              "rgba(255,255,255,0.7)",
                          }}
                        />

                        <Text
                          style={{
                            color:
                              "rgba(255,255,255,0.6)",
                            fontSize: 11,
                          }}
                        >
                          PRODUCTS
                        </Text>
                      </Space>

                      <div
                        style={{
                          color: "#fff",
                          fontSize: 20,
                          fontWeight: 700,
                          marginTop: 4,
                        }}
                      >
                        {formatNumber(
                          totalProducts
                        )}
                      </div>
                    </div>
                  </Col>

                  {/* TOTAL UNITS */}

                  <Col xs={12} sm={8}>
                    <div
                      style={{
                        background:
                          "rgba(255,255,255,0.08)",
                        borderRadius: 10,
                        padding: 10,
                      }}
                    >
                      <Space size={7}>
                        <BoxPlotOutlined
                          style={{
                            fontSize: 16,
                            color:
                              "rgba(255,255,255,0.7)",
                          }}
                        />

                        <Text
                          style={{
                            color:
                              "rgba(255,255,255,0.6)",
                            fontSize: 11,
                          }}
                        >
                          UNITS
                        </Text>
                      </Space>

                      <div
                        style={{
                          color: "#fff",
                          fontSize: 20,
                          fontWeight: 700,
                          marginTop: 4,
                        }}
                      >
                        {formatNumber(
                          totalUnits
                        )}
                      </div>
                    </div>
                  </Col>

                  {/* LOW STOCK */}

                  <Col xs={12} sm={8}>
                    <div
                      style={{
                        background:
                          "rgba(255,255,255,0.08)",
                        borderRadius: 10,
                        padding: 10,
                      }}
                    >
                      <Space size={7}>
                        <WarningOutlined
                          style={{
                            fontSize: 16,
                            color:
                              "rgba(255,255,255,0.7)",
                          }}
                        />

                        <Text
                          style={{
                            color:
                              "rgba(255,255,255,0.6)",
                            fontSize: 11,
                          }}
                        >
                          LOW STOCK
                        </Text>
                      </Space>

                      <div
                        style={{
                          color: "#fff",
                          fontSize: 20,
                          fontWeight: 700,
                          marginTop: 4,
                        }}
                      >
                        {formatNumber(
                          lowStock
                        )}
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            )}
          </Card>
        </Col>
      ))}
    </Row>
  );
};

export default DashboardCards;